import { Directive, ElementRef, Renderer2, OnDestroy, AfterViewInit, Input } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '../ridango.models';

@Directive({
    selector: '[ridangoAppTrapFocus]'
})
export class TrapFocusDirective implements AfterViewInit, OnDestroy {
    @Input() silentLiveAnnouncer = false;
    private elementsToModify: HTMLElement[] = [];
    private modal: HTMLElement | null = null;
    private didPresentListener?: () => void;
    private didDismissListener?: () => void;
    private additionalElements = [];
    private liveAnnouncerClassName = '.cdk-live-announcer-element';

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    ngAfterViewInit() {
        this.silentLiveAnnouncer && this.additionalElements.push(this.liveAnnouncerClassName);
        this.modal = this.el.nativeElement.closest('ion-modal');
        if (this.modal) {
            this.listenToModalEvents();
        }
    }

    private listenToModalEvents() {
        if (!this.modal) return;

        // Wait for the modal to be presented
        this.didPresentListener = () => {
            this.setTrap();
        };
        this.modal.addEventListener('didPresent', this.didPresentListener);

        // Listen for modal dismiss event
        this.didDismissListener = () => {
            this.removeTrap();
        };
        this.modal.addEventListener('didDismiss', this.didDismissListener);
    }

    private setTrap() {
        if (!this.modal) return;

        const additionalElements = this.additionalElements
            .map((selector) => Array.from(document.querySelectorAll(selector)))
            .flat() as HTMLElement[];
        const siblings = [
            ...Array.from(this.modal?.parentElement?.children || []).filter((sibling) => sibling !== this.modal)
        ] as HTMLElement[];

        this.elementsToModify = [...siblings, ...additionalElements];
        this.elementsToModify.forEach((element) => {
            this.renderer.setAttribute(element, 'aria-hidden', 'true');
            this.renderer.setAttribute(element, 'inert', 'true');
        });

        // iOS fix for VoiceOver, remove aria-hidden from the modal
        if (Capacitor.getPlatform() === Platform.IOS) {
            this.renderer.removeAttribute(this.modal, 'aria-hidden');
        }
    }

    private removeTrap() {
        this.elementsToModify.forEach((element) => {
            this.renderer.removeAttribute(element, 'aria-hidden');
            this.renderer.removeAttribute(element, 'inert');
        });
    }

    ngOnDestroy() {
        this.removeTrap();

        if (this.modal && this.didPresentListener) {
            this.modal.removeEventListener('didPresent', this.didPresentListener);
        }
        if (this.modal && this.didDismissListener) {
            this.modal.removeEventListener('didDismiss', this.didDismissListener);
        }
    }
}
